import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import Navigation from './components/Navigation'
import Login from './pages/Login'
import Logout from './pages/Logout'
import useUserStore from './store/useUserStore'
import useNotificationStore from './store/useNotificationStore'
import { VersionConflictModal } from './components/common/VersionConflictModal'
import ErrorBoundary from './components/common/ErrorBoundary'

const ProjectList = lazy(() => import('./pages/ProjectList'))
const ProjectSettings = lazy(() => import('./pages/ProjectSettings'))
const TaskList = lazy(() => import('./pages/TaskList'))
const TaskBoard = lazy(() => import('./pages/TaskBoard'))
const Gantt = lazy(() => import('./pages/Gantt'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const MyTasks = lazy(() => import('./pages/MyTasks'))
const Profile = lazy(() => import('./pages/Profile'))
const Audit = lazy(() => import('./pages/Audit'))
const Help = lazy(() => import('./pages/Help'))
const Register = lazy(() => import('./pages/Register'))
const Signup = lazy(() => import('./pages/Signup'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'))
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))

function LoadingFallback() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function ProtectedRoute({ children }) {
  const { authToken } = useUserStore()
  const location = useLocation()

  if (!authToken) {
    // Include current path as returnTo parameter so user returns here after login
    const returnTo = location.pathname !== '/login' ? `?returnTo=${encodeURIComponent(location.pathname)}` : ''
    return <Navigate to={`/login${returnTo}`} replace />
  }

  return children
}

function App() {
  const location = useLocation()
  const { authToken } = useUserStore()
  const { startPolling, stopPolling } = useNotificationStore()
  const isLoginPage = location.pathname === '/login'
  const isLogoutPage = location.pathname === '/logout' || location.pathname === '/logoff'
  const isRegisterPage = location.pathname === '/register' || location.pathname.startsWith('/register/')
  const isSignupPage = location.pathname === '/signup'
  const isForgotPasswordPage = location.pathname === '/forgot-password'
  const isResetPasswordPage = location.pathname.startsWith('/reset-password/')
  const isOAuthCallbackPage = location.pathname === '/auth/callback'
  const isPrivacyPolicyPage = location.pathname === '/privacy'
  const isTermsOfServicePage = location.pathname === '/terms'
  const isUnsubscribePage = location.pathname.startsWith('/unsubscribe/')

  const isProjectListPage = location.pathname === '/' || location.pathname === '/projects'
  const isProjectSettingsPage = location.pathname.includes('/projects/') && location.pathname.includes('/settings')
  const isTaskListPage = location.pathname.startsWith('/projects/') && location.pathname.includes('/tasks')
  const isTaskBoardPage = location.pathname.startsWith('/projects/') && location.pathname.includes('/board')
  const isGanttPage = location.pathname.startsWith('/projects/') && location.pathname.includes('/gantt')
  const isDashboardPage = location.pathname.startsWith('/projects/') && location.pathname.includes('/dashboard')
  const isMyTasksPage = location.pathname === '/my-tasks'
  const isProfilePage = location.pathname === '/profile'
  const isAuditPage = location.pathname === '/audit'
  const isHelpPage = location.pathname === '/help'

  // Start/stop notification polling based on authentication state
  useEffect(() => {
    if (authToken) {
      startPolling()
    } else {
      stopPolling()
    }
    // Cleanup on unmount
    return () => {
      stopPolling()
    }
  }, [authToken, startPolling, stopPolling])

  // Hide navigation on login, logout, register, signup, forgot/reset password, OAuth callback, privacy, terms, and unsubscribe pages
  const showNavigation = !isLoginPage && !isLogoutPage && !isRegisterPage && !isSignupPage && !isForgotPasswordPage && !isResetPasswordPage && !isOAuthCallbackPage && !isPrivacyPolicyPage && !isTermsOfServicePage && !isUnsubscribePage && authToken

  return (
    <>
      {showNavigation && <Navigation />}
      <main className={isProjectListPage || isProjectSettingsPage || isTaskListPage || isTaskBoardPage || isGanttPage || isDashboardPage || isMyTasksPage || isProfilePage || isAuditPage || isHelpPage || isLoginPage || isLogoutPage || isRegisterPage || isSignupPage || isForgotPasswordPage || isResetPasswordPage || isOAuthCallbackPage || isPrivacyPolicyPage || isTermsOfServicePage || isUnsubscribePage ? `h-screen flex flex-col ${showNavigation ? 'pt-16' : ''}` : `container mx-auto flex min-h-screen items-center justify-center ${showNavigation ? 'pt-16' : ''} px-4`}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/logoff" element={<Logout />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/:inviteCode" element={<Register />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<OAuthCallback />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
              <Route path="/" element={<ProtectedRoute><ProjectList /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute><ProjectList /></ProtectedRoute>} />
              <Route path="/projects/:projectId/settings" element={<ProtectedRoute><ProjectSettings /></ProtectedRoute>} />
              <Route path="/projects/:projectId/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/projects/:projectId/tasks" element={<ProtectedRoute><TaskList /></ProtectedRoute>} />
              <Route path="/projects/:projectId/board" element={<ProtectedRoute><TaskBoard /></ProtectedRoute>} />
              <Route path="/projects/:projectId/gantt" element={<ProtectedRoute><Gantt /></ProtectedRoute>} />
              <Route path="/my-tasks" element={<ProtectedRoute><MyTasks /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/audit" element={<ProtectedRoute><Audit /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <VersionConflictModal />
    </>
  )
}

export default App
